/**
 * CommissionsCockpit — Cockpit-language structural rewrite of /commissions.
 * Wraps the existing Commissions logic in Cockpit shell primitives.
 * No business logic changes.
 */
import { useState, useMemo, useEffect } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { supabase } from "@/lib/supabase";
import {
  computeRepScorecard, TIER_CONFIG, BASE_COMMISSION_RATE, CLOSE_RATE_BONUSES,
  SAME_DAY_CLOSE_BONUS, FINANCING_BONUS_FLAT, RECOVERY_BONUS_FLAT,
  type RepScorecard,
} from "@/data/commissionEngine";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitKpiStrip, CockpitTabs, CockpitSection, CockpitFinancialTable,
  type CockpitColumn, CockpitEmpty, CockpitListShell, CockpitColumns, CockpitRow,
} from "@/components/cockpit/CockpitShell";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";

const currency = (v: number) => `$${Math.round(v).toLocaleString()}`;
const pct = (v: number) => `${Math.round(v * 100)}%`;

export default function CommissionsCockpit() {
  const { appointmentResults, followUpTasks, jobs } = useAppData();
  const fin$ = useFinancialPermissions();
  const { repName: CURRENT_REP } = useCurrentRep();
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const scopedResults = fin$.scopedToOwn
    ? appointmentResults.filter((r) => r.assignedRep === CURRENT_REP)
    : appointmentResults;
  const [selectedRep, setSelectedRep] = useState<string | null>(fin$.scopedToOwn ? CURRENT_REP : null);
  const [tab, setTab] = useState("by-rep");

  const reps = useMemo(() => [...new Set(scopedResults.map(r => r.assignedRep))].sort(), [scopedResults]);
  const scorecards = useMemo((): RepScorecard[] => reps.map(rep => {
    const repTasks = followUpTasks.filter(t => t.assignedTo === rep);
    const completed = repTasks.filter(t => t.status === "completed");
    const followUpRate = repTasks.length > 0 ? completed.length / repTasks.length : 1.0;
    return computeRepScorecard(rep, scopedResults, followUpRate, jobs);
  }), [reps, scopedResults, followUpTasks, jobs]);

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          scopedResults
            .map((r) => (r.assignedRep || "").trim())
            .filter((v) => isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }

      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);

      if (cancelled) return;

      const map = new Map<string, string>();
      const rows = [
        ...((byIdRes.data ?? []) as Array<Record<string, unknown>>),
        ...((byAuthIdRes.data ?? []) as Array<Record<string, unknown>>),
      ];
      for (const row of rows) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const fullName = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        const id = String(row.id ?? "").trim();
        const authUserId = String(row.auth_user_id ?? "").trim();
        if (fullName) {
          if (id) map.set(id, fullName);
          if (authUserId) map.set(authUserId, fullName);
        }
      }
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [scopedResults]);

  const getRepDisplayName = (rep: string) => {
    const resolved = resolveProfileDisplayName(rep, repNameById, "Unassigned");
    if (resolved.toLowerCase() === "system") return "System";
    return resolved;
  };

  const totals = useMemo(() => ({
    totalCommission: scorecards.reduce((s, sc) => s + sc.totalEarned, 0),
    totalRevenue: scorecards.reduce((s, sc) => s + sc.totalRevenueSold, 0),
    totalDeals: scorecards.reduce((s, sc) => s + sc.salesClosed, 0),
    totalBase: scorecards.reduce((s, sc) => s + sc.totalBaseCommission, 0),
    totalSameDay: scorecards.reduce((s, sc) => s + sc.totalSameDayBonus, 0),
    totalFinancing: scorecards.reduce((s, sc) => s + sc.totalFinancingBonus, 0),
    totalRecovery: scorecards.reduce((s, sc) => s + sc.totalRecoveryBonus, 0),
  }), [scorecards]);

  const activeScorecard = selectedRep ? scorecards.find(s => s.repName === selectedRep) : null;

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Commissions"
        right={
          <>
            <HeroMetric label="Total Payout" value={currency(totals.totalCommission)} hero tone="good" />
            <HeroDivider />
            <HeroMetric label="Deals Paid" value={totals.totalDeals} />
            <HeroDivider />
            <HeroMetric
              label="Effective Rate"
              value={totals.totalRevenue > 0 ? pct(totals.totalCommission / totals.totalRevenue) : "0%"}
            />
          </>
        }
      />

      <CockpitKpiStrip
        items={[
          { label: "Revenue Basis", value: currency(totals.totalRevenue) },
          { label: "Base Commission", value: currency(totals.totalBase), hint: `${pct(BASE_COMMISSION_RATE)} rate` },
          { label: "Same-Day Bonuses", value: currency(totals.totalSameDay), tone: "good", hint: `+${pct(SAME_DAY_CLOSE_BONUS)} per deal` },
          { label: "Financing Bonuses", value: currency(totals.totalFinancing), hint: `${currency(FINANCING_BONUS_FLAT)} per deal` },
          { label: "Recovery Bonuses", value: currency(totals.totalRecovery), tone: "warn", hint: `${currency(RECOVERY_BONUS_FLAT)} per deal` },
        ]}
      />

      <CockpitSection eyebrow="Reference" title="Commission Structure">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60 border border-border/60">
          <div className="bg-background px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">Base Rate</p>
            <p className="text-sm tabular-nums">{pct(BASE_COMMISSION_RATE)} of revenue</p>
          </div>
          {CLOSE_RATE_BONUSES.map((b) => (
            <div key={b.label} className="bg-background px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">{b.label}</p>
              <p className="text-sm tabular-nums">+{pct(b.bonus)} bonus</p>
            </div>
          ))}
        </div>
      </CockpitSection>

      <CockpitTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "by-rep", label: "By Rep", count: scorecards.length },
          { value: "all-deals", label: "All Deals", count: scorecards.reduce((s, sc) => s + sc.deals.length, 0) },
        ]}
      />

      {tab === "by-rep" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-3">
              Reps · {scorecards.length}
            </p>
            {scorecards.length === 0 ? (
              <CockpitEmpty message="No commission data yet." />
            ) : (
              <CockpitListShell>
                <CockpitColumns template="1fr 100px">
                  <span>Rep</span>
                  <span className="text-right">Earned</span>
                </CockpitColumns>
                {scorecards.map((sc) => {
                  const tier = TIER_CONFIG[sc.performanceTier];
                  return (
                    <CockpitRow
                      key={sc.repName}
                      template="1fr 100px"
                      onClick={() => setSelectedRep(sc.repName)}
                      affordance={false}
                    >
                      <div>
                        <p className={cn("text-sm font-medium", selectedRep === sc.repName && "text-foreground")}>{getRepDisplayName(sc.repName)}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{tier.label} · {sc.salesClosed} deals</p>
                      </div>
                      <div className="text-right text-success font-medium tabular-nums">{currency(sc.totalEarned)}</div>
                    </CockpitRow>
                  );
                })}
              </CockpitListShell>
            )}
          </div>

          <div className="lg:col-span-2">
            {activeScorecard ? (
              <div className="border border-border/60">
                <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold tracking-tight">{getRepDisplayName(activeScorecard.repName)}</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                      {pct(activeScorecard.effectiveRate)} effective · {pct(BASE_COMMISSION_RATE)} base + {pct(activeScorecard.closeRateBonusRate)} bonus
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold tabular-nums text-success">{currency(activeScorecard.totalEarned)}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Total Earned</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 divide-x divide-border/60 border-b border-border/60">
                  {[
                    { label: "Base", value: currency(activeScorecard.totalBaseCommission) },
                    { label: "Same-Day", value: currency(activeScorecard.totalSameDayBonus), cls: "text-success" },
                    { label: "Financing", value: currency(activeScorecard.totalFinancingBonus) },
                    { label: "Recovery", value: currency(activeScorecard.totalRecoveryBonus), cls: "text-warning" },
                  ].map((b) => (
                    <div key={b.label} className="px-4 py-3">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{b.label}</p>
                      <p className={cn("text-sm font-medium tabular-nums", b.cls)}>{b.value}</p>
                    </div>
                  ))}
                </div>
                {activeScorecard.deals.length > 0 ? (
                  <CockpitFinancialTable
                    columns={[
                      { key: "customer", label: "Customer", render: (d: typeof activeScorecard.deals[0]) => <span className="font-medium">{d.customerName}</span> },
                      { key: "date", label: "Date", render: (d) => <span className="text-muted-foreground">{format(parseISO(d.appointmentDate), "MMM d")}</span> },
                      { key: "rev", label: "Revenue", numeric: true, render: (d) => currency(d.revenue) },
                      { key: "base", label: "Base", numeric: true, render: (d) => currency(d.baseAmount) },
                      { key: "sd", label: "Same-Day", numeric: true, render: (d) => d.sameDayBonus > 0 ? <span className="text-success">{currency(d.sameDayBonus)}</span> : "—" },
                      { key: "fin", label: "Financing", numeric: true, render: (d) => d.financingBonus > 0 ? currency(d.financingBonus) : "—" },
                      { key: "rec", label: "Recovery", numeric: true, render: (d) => d.recoveryBonus > 0 ? <span className="text-warning">{currency(d.recoveryBonus)}</span> : "—" },
                      { key: "total", label: "Total", numeric: true, render: (d) => <span className="text-success font-medium">{currency(d.totalDeal)}</span> },
                    ] as CockpitColumn<typeof activeScorecard.deals[0]>[]}
                    rows={activeScorecard.deals}
                    getKey={(d) => d.resultId}
                  />
                ) : (
                  <CockpitEmpty message="No commission deals recorded." />
                )}
              </div>
            ) : (
              <CockpitEmpty message="Select a rep to view per-deal breakdown." />
            )}
          </div>
        </div>
      )}

      {tab === "all-deals" && (() => {
        const allDeals = scorecards.flatMap(sc =>
          sc.deals.map(d => ({ ...d, repName: sc.repName }))
        ).sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime());
        return (
          <CockpitFinancialTable
            columns={[
              { key: "rep", label: "Rep", render: (d: typeof allDeals[0]) => <span className="font-medium">{getRepDisplayName(d.repName)}</span> },
              { key: "customer", label: "Customer", render: (d) => d.customerName },
              { key: "date", label: "Date", render: (d) => <span className="text-muted-foreground">{format(parseISO(d.appointmentDate), "MMM d")}</span> },
              { key: "rev", label: "Revenue", numeric: true, render: (d) => currency(d.revenue) },
              { key: "base", label: "Base", numeric: true, render: (d) => currency(d.baseAmount) },
              { key: "bonus", label: "Bonuses", numeric: true, render: (d) => currency(d.sameDayBonus + d.financingBonus + d.recoveryBonus) },
              { key: "total", label: "Total", numeric: true, render: (d) => <span className="text-success font-medium">{currency(d.totalDeal)}</span> },
            ] as CockpitColumn<typeof allDeals[0]>[]}
            rows={allDeals}
            getKey={(d) => d.resultId}
            empty="No deals to display."
          />
        );
      })()}
    </CockpitPage>
  );
}
